import { z } from "zod"
import { voiceAPI } from "../electronAPIs/voice"
import { clientContext, controlHost, currentQueueRoute } from "../appControls/bridge"
import { queueModeManager } from "../components/sidebar/queueModeManager"
import type { VoiceController } from "./controller"
import { VoiceProtocol, type TranscriptFragment } from "./protocol"
import { voiceStartupContext } from "./orientation"

interface VoiceState {
    phase: "idle" | "connecting" | "live" | "closing" | "error"
    muted: boolean
    error: string | null
    audioBlocked: boolean
    captions: readonly TranscriptFragment[]
    working: number
    result: string
    seconds: number
    confirmation?: { phrase: string; detail: string }
    finalized: boolean
    controller?: { nodeId: string; workspaceId: string; taskId: string }
}
const initial: VoiceState = {
    phase: "idle",
    muted: false,
    error: null,
    audioBlocked: false,
    captions: [],
    working: 0,
    result: "",
    seconds: 0,
    finalized: false,
}
const envelope = z.object({
    type: z.string(),
    client_event_id: z.string().nullable().optional(),
    usage: z.object({ seconds: z.number() }).optional(),
    error: z.object({ message: z.string(), code: z.string().nullable().optional() }).optional(),
})
const message = (error: unknown) => (error instanceof Error ? error.message : "Voice operation failed")

function waitForIce(peer: RTCPeerConnection, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        const finish = (error?: Error) => {
            clearTimeout(timer)
            peer.removeEventListener("icegatheringstatechange", update)
            signal.removeEventListener("abort", cancel)
            if (error) reject(error)
            else resolve()
        }
        const update = () => {
            if (peer.iceGatheringState === "complete") finish()
        }
        const cancel = () => finish(new Error("Voice connection cancelled"))
        const timer = setTimeout(() => finish(new Error("Voice network negotiation timed out")), 10_000)
        peer.addEventListener("icegatheringstatechange", update)
        signal.addEventListener("abort", cancel, { once: true })
        if (signal.aborted) cancel()
        else update()
    })
}

export class VoiceManager {
    private state: VoiceState = initial
    private listeners = new Set<() => void>()
    private protocol = new VoiceProtocol()
    private peer?: RTCPeerConnection
    private channel?: RTCDataChannel
    private microphone?: MediaStream
    private audio?: HTMLAudioElement
    private controller?: VoiceController
    private starting = false
    private lifetime?: AbortController
    private timeout?: ReturnType<typeof setTimeout>
    private pendingDelegations = new Set<ReturnType<typeof setTimeout>>()
    private startupAppends?: Set<string>
    private lastContext = ""
    private confirmation?: { phrase: string; revision: number; decide: (accepted: boolean) => void }

    snapshot = (): VoiceState => this.state
    subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener)
        return () => this.listeners.delete(listener)
    }
    private update(patch: Partial<VoiceState>) {
        this.state = { ...this.state, ...patch }
        for (const listener of this.listeners) listener()
    }

    async start(voice: string): Promise<void> {
        if (this.starting || ["connecting", "live", "closing"].includes(this.state.phase)) return
        const host = controlHost()
        const workspaceId = host.workspaceId ?? host.store.repos.repos[0]?.id
        if (!workspaceId || host.store.isGuest) {
            this.update({ phase: "error", error: "Select one of your projects to start Conversation mode" })
            return
        }
        const nodeId = host.computers.state.activeNodeId
        const agentConfig = {
            harnessId: host.store.defaultHarnessId,
            model: host.store.defaultModel,
            thinking: host.store.defaultThinking,
            permissionMode: host.store.personalSettingsStore?.settings.current.permissionMode ?? "auto",
        }
        this.starting = true
        this.protocol = new VoiceProtocol()
        this.lastContext = ""
        this.state = { ...initial, phase: "connecting" }
        this.update({})
        const lifetime = new AbortController()
        this.lifetime = lifetime
        this.timeout = setTimeout(() => this.fail("Voice connection timed out"), 60_000)
        try {
            const status = await voiceAPI.status()
            if (!status.hasKey) throw new Error("Add an OpenAI API key in Settings → Voice")
            lifetime.signal.throwIfAborted()
            await voiceAPI.prepare()
            lifetime.signal.throwIfAborted()
            const microphone = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false })
            if (lifetime.signal.aborted) {
                microphone.getTracks().forEach((track) => track.stop())
                return
            }
            this.microphone = microphone
            for (const track of microphone.getAudioTracks()) {
                track.enabled = false
                track.onended = () => {
                    if (this.state.phase === "live") this.fail("Microphone disconnected")
                }
            }
            const connection = await host.computers.backend.getConnection(nodeId)
            lifetime.signal.throwIfAborted()
            const { VoiceController } = await import("./controller")
            lifetime.signal.throwIfAborted()
            const controller = await VoiceController.create(
                {
                    connection,
                    workspaceId,
                    selectedTaskId: host.taskId,
                    ...agentConfig,
                },
                host.store
            )
            if (lifetime.signal.aborted) {
                controller.closeWhenIdle()
                return
            }
            this.controller = controller
            this.update({ controller: { nodeId, workspaceId, taskId: controller.taskId } })
            const peer = new RTCPeerConnection()
            this.peer = peer
            this.audio = new Audio()
            this.audio.autoplay = true
            peer.ontrack = (event) => {
                if (!this.audio) return
                this.audio.srcObject = new MediaStream([event.track])
                void this.resumeAudio()
            }
            peer.onconnectionstatechange = () => {
                if (peer.connectionState === "failed") this.fail("Voice connection lost; final usage is unconfirmed")
            }
            for (const track of microphone.getAudioTracks()) peer.addTrack(track, microphone)
            const channel = peer.createDataChannel("oai-events")
            this.channel = channel
            channel.onmessage = ({ data }: MessageEvent<unknown>) => {
                if (typeof data !== "string") return
                try {
                    this.receive(JSON.parse(data))
                } catch {
                    this.update({ error: "Received an invalid voice event" })
                }
            }
            channel.onclose = () => {
                if (!this.state.finalized) this.fail("Voice disconnected; final usage is unconfirmed")
            }
            channel.onerror = () => this.fail("Voice event connection failed")
            await peer.setLocalDescription(await peer.createOffer())
            await waitForIce(peer, lifetime.signal)
            lifetime.signal.throwIfAborted()
            const sdp = peer.localDescription?.sdp
            if (!sdp) throw new Error("Missing voice connection offer")
            const answer = await voiceAPI.create({ sdp, voice })
            lifetime.signal.throwIfAborted()
            await peer.setRemoteDescription({ type: "answer", sdp: answer.sdp })
        } catch (error) {
            if (!lifetime.signal.aborted) this.fail(message(error))
        } finally {
            if (lifetime.signal.aborted) {
                await voiceAPI.release().catch(() => {})
                if (this.state.phase === "closing") this.update({ phase: "idle" })
            }
            this.starting = false
        }
    }

    private receive(raw: unknown): void {
        const event = envelope.safeParse(raw)
        if (!event.success) return
        if (event.data.usage) this.update({ seconds: event.data.usage.seconds })
        if (event.data.type === "session.closed") {
            this.update({ phase: "idle", finalized: true })
            this.cleanup()
            return
        }
        if (event.data.type === "error") {
            const error = event.data.error?.message ?? "OpenAI rejected a voice command"
            if (this.state.phase === "connecting") this.fail(error)
            else this.update({ error })
            return
        }
        if (event.data.type === "session.started" && this.state.phase === "connecting" && !this.startupAppends) {
            this.startupAppends = new Set()
            if (this.controller) {
                for (const context of voiceStartupContext(this.controller.briefing)) this.append("thinking", null, context)
            }
        }
        if (
            event.data.type === "session.thinking.appended" &&
            this.state.phase === "connecting" &&
            event.data.client_event_id &&
            this.startupAppends?.delete(event.data.client_event_id) &&
            this.startupAppends.size === 0
        ) {
            clearTimeout(this.timeout)
            this.update({ phase: "live" })
            this.contextChanged()
            for (const track of this.microphone?.getAudioTracks() ?? []) track.enabled = !this.state.muted
        }
        if (this.state.phase !== "live") return
        const accepted = this.protocol.accept(raw, () => {
            try {
                return clientContext()
            } catch {
                // The client may be switching computers while speech arrives.
                return undefined
            }
        })
        if (accepted.transcript) {
            this.update({ captions: this.protocol.fragments.slice(-200) })
            if (accepted.transcript.speaker === "user") {
                queueModeManager.recordUserActivity()
                try {
                    const taskId = controlHost().taskId
                    if (taskId) currentQueueRoute(taskId)?.hold()
                } catch {
                    /* The prior route already released its Q countdown. */
                }
            }
        }
        if (accepted.delegationId) {
            const id = accepted.delegationId
            // Let already-in-flight transcript fragments arrive; this timer never
            // treats silence or a transcript fragment as an execution trigger.
            const timer = setTimeout(() => {
                this.pendingDelegations.delete(timer)
                void this.delegate(id)
            }, 500)
            this.pendingDelegations.add(timer)
        }
    }

    private async delegate(id: string): Promise<void> {
        const controller = this.controller
        if (!controller || this.state.phase !== "live") return
        const protocol = this.protocol
        try {
            const work = protocol.prepare(id, clientContext())
            if (!work) {
                this.append("commentary", id, "I did not receive a new transcribed request. Please repeat the command.")
                return
            }
            const spoken = work.userSpeech
                .toLocaleLowerCase()
                .replace(/[^\p{L}\p{N} ]/gu, "")
                .replace(/\s+/g, " ")
                .trim()
            if (this.confirmation && work.revision > this.confirmation.revision && (spoken === this.confirmation.phrase || spoken === "cancel")) {
                const accepted = spoken !== "cancel"
                this.confirmation.decide(accepted)
                this.append(
                    "thinking",
                    id,
                    accepted
                        ? "The user explicitly confirmed the displayed operation. Execution is still pending."
                        : "The user cancelled the displayed operation."
                )
                return
            }
            this.update({ working: this.state.working + 1 })
            try {
                const response = await controller.run(work.input, work.requestId)
                if (this.protocol !== protocol || this.state.phase !== "live") return
                this.update({ result: response })
                // Long controller results remain available in the Admin task.
                this.append(protocol.revision === work.revision ? "commentary" : "thinking", id, response.slice(0, 3600))
            } finally {
                if (this.protocol === protocol) this.update({ working: Math.max(0, this.state.working - 1) })
            }
        } catch (error) {
            if (this.protocol !== protocol || this.state.phase !== "live") return
            this.update({ error: message(error) })
            this.append("commentary", id, ("The controller reported: " + message(error)).slice(0, 3600))
        }
    }

    private append(kind: "thinking" | "commentary", delegationId: string | null, text: string): void {
        if (this.channel?.readyState !== "open" || (this.state.phase !== "live" && this.state.phase !== "connecting")) return
        // <=450 UTF-8 bytes is conservatively below the API's 500-token limit,
        // including non-Latin speech. Content budgets belong to each source.
        let chunk = ""
        let bytes = 0
        const encoder = new TextEncoder()
        for (const char of text) {
            const size = encoder.encode(char).length
            if (bytes + size > 450) {
                this.sendAppend(kind, delegationId, chunk)
                chunk = ""
                bytes = 0
            }
            chunk += char
            bytes += size
        }
        if (chunk) this.sendAppend(kind, delegationId, chunk)
    }
    private sendAppend(kind: "thinking" | "commentary", delegationId: string | null, content: string) {
        const eventId = crypto.randomUUID()
        if (this.state.phase === "connecting") this.startupAppends?.add(eventId)
        this.channel?.send(JSON.stringify({ type: `session.${kind}.append`, event_id: eventId, delegation_id: delegationId, content }))
    }
    contextChanged(): void {
        if (this.state.phase !== "live") return
        try {
            const context = clientContext()
            const summary = JSON.stringify({
                nodeId: context.nodeId,
                workspaceId: context.workspaceId,
                taskId: context.taskId,
                path: context.path,
                queue: context.queue,
            })
            if (summary !== this.lastContext) {
                this.lastContext = summary
                this.append("thinking", null, summary.slice(0, 3600))
            }
        } catch {
            /* Navigation briefly removes the active client surface. */
        }
    }
    permissionSignal(nodeId: string, sessionId: string): AbortSignal | undefined {
        if (this.state.controller?.nodeId === nodeId && this.controller?.taskId === sessionId) return this.lifetime?.signal
        return undefined
    }
    requestConfirmation(sessionId: string, label: string, detail: string, decide: (accepted: boolean) => void): () => void {
        if (this.state.phase !== "live" || this.controller?.taskId !== sessionId || this.confirmation) return () => {}
        const phrase = ("confirm " + label)
            .toLocaleLowerCase()
            .replace(/[^\p{L}\p{N} ]/gu, "")
            .replace(/\s+/g, " ")
            .trim()
        const pending = { phrase, decide, revision: this.protocol.revision }
        this.confirmation = pending
        this.update({ confirmation: { phrase, detail } })
        this.append("commentary", null, (detail + '. To approve, say "' + phrase + '", or say "cancel".').slice(0, 3600))
        return () => {
            if (this.confirmation === pending) {
                this.confirmation = undefined
                this.update({ confirmation: undefined })
            }
        }
    }
    toggleMute(): void {
        const muted = !this.state.muted
        for (const track of this.microphone?.getAudioTracks() ?? []) track.enabled = this.state.phase === "live" && !muted
        this.update({ muted })
    }
    async resumeAudio(): Promise<void> {
        try {
            await this.audio?.play()
            this.update({ audioBlocked: false })
        } catch {
            this.update({ audioBlocked: true })
        }
    }
    dismiss(): void {
        if (!["connecting", "live", "closing"].includes(this.state.phase)) {
            this.state = initial
            this.update({})
        }
    }
    end(): void {
        if (this.state.phase === "closing") return
        if (this.starting) {
            this.update({ phase: "closing" })
            this.cleanup()
            return
        }
        if (this.state.phase === "live" && this.channel?.readyState === "open") {
            this.update({ phase: "closing", muted: true })
            for (const track of this.microphone?.getAudioTracks() ?? []) track.enabled = false
            this.channel.send(JSON.stringify({ type: "session.close" }))
            this.timeout = setTimeout(() => this.fail("Call ended; final usage is unconfirmed"), 15_000)
        } else {
            this.update({ phase: "idle" })
            this.cleanup()
        }
    }
    private fail(error: string): void {
        this.update({ phase: "error", error })
        this.cleanup()
    }
    private cleanup(): void {
        this.confirmation?.decide(false)
        this.confirmation = undefined
        this.update({ confirmation: undefined })
        this.lifetime?.abort()
        clearTimeout(this.timeout)
        for (const timer of this.pendingDelegations) clearTimeout(timer)
        this.pendingDelegations.clear()
        this.startupAppends = undefined
        if (this.channel) {
            this.channel.onclose = null
            this.channel.onerror = null
            this.channel.onmessage = null
            this.channel.close()
        }
        if (this.peer) {
            this.peer.onconnectionstatechange = null
            this.peer.ontrack = null
            this.peer.close()
        }
        for (const track of this.microphone?.getTracks() ?? []) {
            track.onended = null
            track.stop()
        }
        if (this.audio) {
            this.audio.pause()
            this.audio.srcObject = null
        }
        this.controller?.closeWhenIdle()
        this.controller = undefined
        this.channel = undefined
        this.peer = undefined
        this.microphone = undefined
        this.audio = undefined
        void voiceAPI.release().catch(() => {})
    }
}
// Deliberately outside the node-keyed React tree: changing tasks or computers
// replaces the UI bridge, not the call or its pinned controller connection.
export const voiceManager = new VoiceManager()
